import React, { useEffect } from 'react';
import {
    Box,
    Container,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Hidden,
    Avatar,
} from '@mui/material';
import { Check, Close, Report, Block } from '@mui/icons-material';
import ConfirmationDialog from '../../../components/confirm-dialog';
import Api from '../../../store/api';
import { useLocalState } from '../../../hooks';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './received-interests.css';

interface InterestItem {
    id: number;
    fromId: number;
    toId: number;
    state: string;
    message: string;
    createdAt: string;
    viewedAt: string | null;
    acceptedAt: string | null;
    rejectedAt: string | null;
    updatedAt: string;
}

interface ProfileInfo {
    id: number;
    memberId?: string;
    firstName?: string;
    lastName?: string;
    age?: number;
    state?: string;
    city?: string;
    img?: string;
}

const ReceivedInterests: React.FC = () => {
    const navigate = useNavigate();
    const [LS, setLS] = useLocalState({
        interests: [] as InterestItem[],
        loading: false,
        error: '',
        confirmOpen: false,
        selectedInterestId: null as number | null,
        selectedAction: '' as string, // 'accept' | 'reject' | 'block' | 'report'
        isSubmitting: false,
        profilesById: {} as Record<number, ProfileInfo>,
    });

    const authUserId = useSelector((state: any) => state.auth?.user?.id);

    useEffect(() => {
        if (authUserId) {
            fetchReceivedInterests();
        }
    }, [authUserId]);

    const fetchReceivedInterests = async () => {
        if (!authUserId) return;
        setLS({ loading: true, error: '' });
        try {
            const response = await Api.getInterestReceived(authUserId);
            if (response?.data?.data) {
                const interests: InterestItem[] = response.data.data;
                const uniqueUserIds = Array.from(new Set(interests.map((item) => item.fromId)));
                const profileResults = await Promise.all(
                    uniqueUserIds.map(async (userId) => {
                        try {
                            const profile = await Api.getProfileDetails(userId);
                            return [userId, profile] as const;
                        } catch (e) {
                            return [userId, null] as const;
                        }
                    })
                );
                const profilesById = profileResults.reduce((acc, [userId, profile]) => {
                    if (profile) acc[userId] = profile;
                    return acc;
                }, {} as Record<number, ProfileInfo>);

                setLS({ interests, profilesById, loading: false });
            } else {
                setLS({ error: 'Failed to fetch received interests', loading: false });
            }
        } catch (error: any) {
            setLS({ error: error.message || 'Error fetching interests', loading: false });
        }
    };

    const handleAction = (interestId: number, action: 'accept' | 'reject' | 'block' | 'report') => {
        setLS({ selectedInterestId: interestId, selectedAction: action, confirmOpen: true });
    };

    const confirmAction = async () => {
        if (LS.selectedInterestId && authUserId && LS.selectedAction) {
            setLS({ isSubmitting: true });
            try {
                const interest = LS.interests.find((i: InterestItem) => i.id === LS.selectedInterestId);
                if (interest) {
                    const now = new Date().toISOString();
                    const nextState =
                        LS.selectedAction === 'accept'
                            ? 'ACCEPTED'
                            : LS.selectedAction === 'reject'
                            ? 'REJECTED'
                            : LS.selectedAction === 'block'
                            ? 'BLOCKED'
                            : 'REPORTED';

                    await Api.updateInterestState(
                        interest.fromId,
                        authUserId,
                        nextState
                    );

                    // Optimistically reflect the state change immediately in UI.
                    setLS({
                        interests: LS.interests.map((item: InterestItem) =>
                            item.id === LS.selectedInterestId
                                ? {
                                      ...item,
                                      state: nextState,
                                      acceptedAt: nextState === 'ACCEPTED' ? now : item.acceptedAt,
                                      rejectedAt: nextState === 'REJECTED' ? now : item.rejectedAt,
                                  }
                                : item
                        ),
                        isSubmitting: false,
                    });

                    // Keep data in sync with backend response timestamps.
                    fetchReceivedInterests();
                }
            } catch (error: any) {
                setLS({ isSubmitting: false, error: error.message || `Failed to ${LS.selectedAction} interest` });
            }
        }
        setLS({ confirmOpen: false, selectedInterestId: null, selectedAction: '' });
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getStatusDisplay = (interest: InterestItem) => {
        if (interest.acceptedAt) return { text: 'ACCEPTED', color: 'success' };
        if (interest.rejectedAt) return { text: 'REJECTED', color: 'error' };
        if (interest.state === 'BLOCKED') return { text: 'BLOCKED', color: 'error' };
        if (interest.state === 'REPORTED') return { text: 'REPORTED', color: 'warning' };
        if (interest.state === 'VIEWED') return { text: 'VIEWED', color: 'info' };
        return { text: 'NEW', color: 'warning' };
    };

    const canRespond = (interest: InterestItem) => {
        return (
            !interest.acceptedAt &&
            !interest.rejectedAt &&
            interest.state !== 'BLOCKED' &&
            interest.state !== 'REPORTED'
        );
    };

    const openProfile = async (interest: InterestItem) => {
        const profile = LS.profilesById[interest.fromId];
        if (!profile) return;

        if (authUserId && interest.state === 'SENT') {
            try {
                const now = new Date().toISOString();
                await Api.updateInterestState(interest.fromId, authUserId, 'VIEWED');
                setLS({
                    interests: LS.interests.map((item: InterestItem) =>
                        item.id === interest.id ? { ...item, state: 'VIEWED', viewedAt: now } : item
                    ),
                });
            } catch (error) {
                // Keep navigation flow even if viewed-state update fails.
            }
        }

        sessionStorage.setItem('selectedProfile', JSON.stringify(profile));
        navigate('/user/view-profile');
    };

    return (
        <>
            <Hidden mdUp>
                <Box sx={{ height: '50px' }} />
            </Hidden>
            <Box sx={{ p: 3 }}>
                <Container maxWidth="lg">
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                            Received Interests
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Members who sent you interests - accept or reject
                        </Typography>
                    </Box>

                    {LS.error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {LS.error}
                        </Alert>
                    )}

                    {LS.loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : LS.interests.length === 0 ? (
                        <Card sx={{ textAlign: 'center', py: 8 }}>
                            <Typography variant="h6" color="textSecondary">
                                No received interests yet
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                When members send you interests, they'll appear here
                            </Typography>
                        </Card>
                    ) : (
                        <Grid container spacing={3}>
                            {LS.interests.map((interest: InterestItem) => {
                                const status = getStatusDisplay(interest);
                                const profile = LS.profilesById[interest.fromId];
                                const name = `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim() || `Profile #${interest.fromId}`;
                                return (
                                    <Grid item xs={12} md={6} key={interest.id}>
                                        <Card
                                            sx={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
                                            onClick={() => openProfile(interest)}
                                        >
                                            <CardContent sx={{ pb: 1, flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                                    <Box
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
                                                        onClick={() => openProfile(interest)}
                                                    >
                                                        <Avatar src={profile?.img} alt={name} sx={{ width: 44, height: 44 }} />
                                                        <Box>
                                                            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                                {name}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                Profile ID: {profile?.memberId || interest.fromId}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                Age: {profile?.age ? `${profile.age} Years` : '-'}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                {profile?.city || '-'}, {profile?.state || '-'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Chip
                                                        label={status.text}
                                                        color={status.color as any}
                                                        size="small"
                                                    />
                                                </Box>

                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        Received: {formatDate(interest.createdAt)}
                                                    </Typography>
                                                    {interest.acceptedAt && (
                                                        <Typography variant="caption" sx={{ color: '#2e7d32', display: 'block', mt: 0.5 }}>
                                                            ✓ Accepted: {formatDate(interest.acceptedAt)}
                                                        </Typography>
                                                    )}
                                                    {interest.rejectedAt && (
                                                        <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 0.5 }}>
                                                            ✗ Rejected: {formatDate(interest.rejectedAt)}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {interest.message && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                            <strong>Message:</strong>
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                            sx={{
                                                                p: 1,
                                                                bgcolor: '#f5f5f5',
                                                                borderRadius: 1,
                                                                maxHeight: '80px',
                                                                overflow: 'auto',
                                                            }}
                                                        >
                                                            {interest.message}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </CardContent>

                                            <CardActions sx={{ gap: 1 }}>
                                                {canRespond(interest) && (
                                                    <>
                                                        <Button
                                                            size="small"
                                                            startIcon={<Check />}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleAction(interest.id, 'accept');
                                                            }}
                                                            variant="contained"
                                                            sx={{ color: 'white', backgroundColor: '#2e7d32' }}
                                                        >
                                                            Accept
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            startIcon={<Close />}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleAction(interest.id, 'reject');
                                                            }}
                                                            sx={{ color: '#d32f2f' }}
                                                        >
                                                            Reject
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            startIcon={<Block />}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleAction(interest.id, 'block');
                                                            }}
                                                            sx={{ color: '#6d1b7b' }}
                                                        >
                                                            Block
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            startIcon={<Report />}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleAction(interest.id, 'report');
                                                            }}
                                                            sx={{ color: '#ed6c02' }}
                                                        >
                                                            Report
                                                        </Button>
                                                    </>
                                                )}
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Container>
            </Box>

            <ConfirmationDialog
                open={LS.confirmOpen}
                onClose={() => setLS({ confirmOpen: false })}
                onConfirm={confirmAction}
                title={
                    LS.selectedAction === 'accept'
                        ? 'Accept Interest'
                        : LS.selectedAction === 'reject'
                        ? 'Reject Interest'
                        : LS.selectedAction === 'block'
                        ? 'Block Interest'
                        : 'Report Interest'
                }
                message={
                    LS.selectedAction === 'accept'
                        ? 'Do you want to accept this interest?'
                        : LS.selectedAction === 'reject'
                        ? 'Do you want to reject this interest?'
                        : LS.selectedAction === 'block'
                        ? 'Do you want to block this member?'
                        : 'Do you want to report this member?'
                }
                confirmText={
                    LS.selectedAction === 'accept'
                        ? 'Accept'
                        : LS.selectedAction === 'reject'
                        ? 'Reject'
                        : LS.selectedAction === 'block'
                        ? 'Block'
                        : 'Report'
                }
                confirmColor={
                    LS.selectedAction === 'accept'
                        ? 'success'
                        : LS.selectedAction === 'reject' || LS.selectedAction === 'block'
                        ? 'error'
                        : 'warning'
                }
                isLoading={LS.isSubmitting}
            />
        </>
    );
};

export default ReceivedInterests;
